/**
 * service-detail-view.tsx
 *
 * Dedicated view for a published service. Provides:
 * - Abilities management (AI Search, Content Resolution)
 * - Access management (asset sharing)
 * - Test endpoint
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft, Globe, Server, AlertCircle, RotateCcwIcon } from 'lucide-react';
import { useUser } from '@/context/user-provider';
import { serviceManagementService, type AssetServiceWithDetails } from '@/services/service-management-service';
import { assetService, type AssetWithPermission } from '@/services/asset-service';
import abilityService from '@/services/ability-service';
import AssetAbilitiesPanel from '@/components/features/assets/asset-abilities-panel';
import { AssetSharingPanel } from '@/components/features/assets/asset-sharing-panel';
import { ServiceApiKeyPanel } from '@/components/features/services/service-api-key-panel';
import { AbilityTestPanel } from '@/components/features/assets/ability-test-panel';

const buildPermission = (service: AssetServiceWithDetails, currentUserId?: string | null) => {
  const isOwner = !!currentUserId && currentUserId === service.ownerUserId;
  const role: 'owner' | 'editor' | 'viewer' = isOwner ? 'owner' : 'viewer';
  return {
    role,
    canRead: true,
    canUpdate: role !== 'viewer',
    canDelete: role === 'owner',
    canShare: role === 'owner',
  };
};

const ServiceDetailView = () => {
  const { serviceId, tab } = useParams<{ serviceId: string; tab?: string }>();
  const navigate = useNavigate();
  const { user } = useUser();

  const [service, setService] = useState<AssetServiceWithDetails | null>(null);
  const [asset, setAsset] = useState<AssetWithPermission | null>(null);
  const [loading, setLoading] = useState(true);
  const [defaultTab, setDefaultTab] = useState<string>(tab || 'abilities');
  const [abilities, setAbilities] = useState<Record<string, unknown> | undefined>(undefined);

  const applyAsset = async (svc: AssetServiceWithDetails) => {
    if (!svc.asset) {
      setAsset(null);
      return;
    }
    const assetResult = await assetService.getAsset(svc.asset.uuid);
    const permission = buildPermission(svc, user?.id);
    setAsset({
      ...assetResult,
      permission,
    } as AssetWithPermission);

    // Load abilities for this asset
    try {
      const abilitiesData = await abilityService.getAbilities(svc.asset.uuid);
      setAbilities(abilitiesData.abilities);
    } catch (error) {
      console.error('Failed to load abilities', error);
      // Don't fail - abilities are optional
    }
  };

  useEffect(() => {
    const load = async () => {
      if (!serviceId) return;
      try {
        setLoading(true);
        const svc = await serviceManagementService.getService(serviceId);
        setService(svc);
        await applyAsset(svc);
      } catch (error) {
        console.error('Failed to load service', error);
        toast.error(error instanceof Error ? error.message : 'Failed to load service');
        navigate('/console/services');
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceId]);

  // Update defaultTab when the tab param changes
  useEffect(() => {
    if (tab) {
      setDefaultTab(tab);
    }
  }, [tab]);

  const refreshAsset = async () => {
    if (!service) return;
    try {
      await applyAsset(service);
    } catch (error) {
      console.error('Failed to refresh asset', error);
      toast.error(error instanceof Error ? error.message : 'Failed to refresh asset');
    }
  };

  const assetId = useMemo(() => service?.asset?.uuid ?? null, [service]);
  const role = useMemo(() => {
    if (!service || !user?.id) return 'viewer' as const;
    return service.ownerUserId === user.id ? 'owner' : 'viewer';
  }, [service, user?.id]);

  if (loading) {
    return (
      <div className="container mx-auto py-8">
        <Card>
          <CardHeader>
            <CardTitle>Loading service…</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-muted-foreground">Please wait while we load the service details.</div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!service || !assetId || !asset) {
    return (
      <div className="container mx-auto py-8">
        <Card>
          <CardHeader>
            <CardTitle>Service not found</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">
                The requested service could not be found or you do not have access.
              </span>
              <Button variant="outline" onClick={() => navigate('/console/services')}>
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Services
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8 space-y-6">
      {!service.isActive && (
        <Card className="border-yellow-500 bg-yellow-50">
          <CardContent className="flex items-start gap-4 pt-6">
            <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <h3 className="font-semibold text-yellow-900 mb-1">Service Inactive</h3>
              <p className="text-sm text-yellow-800 mb-4">
                This service is currently inactive and cannot be accessed. You can reactivate it from the services list.
              </p>
              <Button
                size="sm"
                variant="outline"
                onClick={() => navigate('/console/services')}
              >
                <RotateCcwIcon className="h-4 w-4 mr-2" />
                Go to Services
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="space-y-1">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Server className="h-4 w-4" />
            Service
          </div>
          <h1 className="text-2xl font-bold">{service.serviceSlug}</h1>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Globe className="h-4 w-4" />
            {service.organization?.name ?? 'Organization service'}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => navigate('/console/services')}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
        </div>
      </div>

      <Tabs value={defaultTab} onValueChange={(value) => navigate(`/console/services/${serviceId}/${value}`)} className="space-y-4">
        <TabsList>
          <TabsTrigger value="abilities">Abilities</TabsTrigger>
          <TabsTrigger value="access">Access</TabsTrigger>
          <TabsTrigger value="test">Test</TabsTrigger>
        </TabsList>

        <TabsContent value="abilities">
          <AssetAbilitiesPanel
            assetId={assetId}
            role={role}
            asset={asset}
            serviceSlug={service.serviceSlug}
            orgSlug={service.organization?.slug}
          />
        </TabsContent>

        <TabsContent value="access" className="space-y-6">
          <section className="space-y-2">
            <h2 className="text-lg font-semibold">People who can access this service</h2>
            <p className="text-sm text-muted-foreground">
              This list shows every collaborator and their role on the underlying asset. Update it to grant or remove
              access for team members.
            </p>
          </section>

          <AssetSharingPanel asset={asset} onShareUpdate={refreshAsset} />

          <section className="space-y-2 border-t pt-6">
            <h2 className="text-lg font-semibold">Programmatic access</h2>
            <p className="text-sm text-muted-foreground">
              Issue service-scoped API keys when you need long-lived integrations. Keys inherit the permissions defined
              above.
            </p>
            <ServiceApiKeyPanel serviceId={service.id} serviceName={service.serviceSlug} />
          </section>
        </TabsContent>

        <TabsContent value="test" className="mt-6">
          <AbilityTestPanel
            assetId={assetId}
            asset={asset}
            serviceSlug={service.serviceSlug}
            orgSlug={service.organization?.slug}
            abilities={abilities}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ServiceDetailView;
